/**
 * buildPlanBuilderStack
 *
 * Dynamic navigation stack for the plan builder.
 * Screens are included/excluded based on what the user
 * has already answered and their selected goal.
 */

import type { PlanBuilderState, PlanBuilderScreen } from "./types";

/** Goals that involve a partner/donor decision */
const PARTNER_GOALS = new Set(["embryo-preserve", "pregnancy"]);

/** Goals where AMH/FSH results are relevant */
const OVARIAN_TEST_GOALS = new Set([
  "preserve",
  "embryo-preserve",
  "pregnancy",
]);

/** Goals where semen analysis is relevant (partner SA for these, or self SA for preserve+AMAB) */
const SA_GOALS = new Set(["embryo-preserve", "pregnancy", "sperm-donation"]);

/**
 * An accepted clinic plan is a different fact from treatment modalities a
 * patient is considering. Ask for it only when the journey can contain a
 * treatment plan; natural trying, donation, adoption, and surrogacy do not
 * have an accepted treatment-plan record in this flow.
 */
export function shouldAskForAcceptedPlan(state: PlanBuilderState): boolean {
  return (
    (state.goal === "pregnancy" && state.stage !== "trying-naturally") ||
    state.goal === "preserve" ||
    state.goal === "embryo-preserve"
  );
}

export function buildPlanBuilderStack(
  state: PlanBuilderState,
): PlanBuilderScreen[] {
  const screens: PlanBuilderScreen[] = [];

  // 1. Goal recap — always first
  screens.push("GoalRecap");

  // 1b. Treatment modality — natural trying is already an explicit
  // non-treatment stage, so do not ask the user to choose IVF/IUI again.
  if (state.goal === "pregnancy" && state.stage !== "trying-naturally") {
    screens.push("ModalityScreen");
  }

  // 1c. Accepted clinic plan — distinct from treatments still under
  // consideration. This captures the actual protocol used downstream.
  if (shouldAskForAcceptedPlan(state)) {
    screens.push("AcceptedPlan");
  }

  // 2. Past cycles — always asked
  screens.push("PastCycles");

  // 2b. Past cycle details — only if they said yes
  if (state.hasPastCycles === true) {
    screens.push("PastCycleDetails");
  }

  // 3. Medical conditions — always asked
  screens.push("Conditions");

  // 4. Ethnicity — always asked
  screens.push("Ethnicity");

  // 5. Partner/donor status — only for relevant goals
  if (state.goal && PARTNER_GOALS.has(state.goal)) {
    screens.push("PartnerStatus");
  }

  // 5b. Clinic status — preserve goals only (egg/embryo/sperm freezing)
  if (state.goal === "preserve" || state.goal === "embryo-preserve") {
    screens.push("ClinicStatus");
  }

  // 6. Known test results — goal + gender specific
  //    preserve + AFAB → ovarian tests; preserve + AMAB → SA
  //    embryo-preserve / pregnancy → both (ovarian + partner SA)
  //    sperm-donation → SA
  const showOvarian =
    state.goal != null &&
    OVARIAN_TEST_GOALS.has(state.goal) &&
    state.gender !== "male";
  const showSA =
    state.goal != null &&
    (SA_GOALS.has(state.goal) ||
      (state.goal === "preserve" && state.gender === "male"));
  if (showOvarian || showSA) {
    screens.push("KnownResults");
  }

  // 7. Location + insurance — always asked
  screens.push("LocationInsurance");

  // 8. Timeline — always asked
  screens.push("Timeline");

  // 9. Free text — always last before building
  screens.push("FreeText");

  // 10. Building animation
  screens.push("Building");

  // 11. Plan reveal — personalized summary
  screens.push("PlanReveal");

  return screens;
}

/**
 * Get the next screen in the stack from the current one.
 * Returns null if at the end.
 */
export function getNextScreen(
  state: PlanBuilderState,
  current: PlanBuilderScreen,
): PlanBuilderScreen | null {
  const stack = buildPlanBuilderStack(state);
  const idx = stack.indexOf(current);
  if (idx === -1 || idx >= stack.length - 1) return null;
  return stack[idx + 1] ?? null;
}

/**
 * Get the previous screen in the stack from the current one.
 * Returns null if at the beginning.
 */
export function getPreviousScreen(
  state: PlanBuilderState,
  current: PlanBuilderScreen,
): PlanBuilderScreen | null {
  const stack = buildPlanBuilderStack(state);
  const idx = stack.indexOf(current);
  if (idx <= 0) return null;
  return stack[idx - 1] ?? null;
}

/**
 * Determine the first screen that hasn't been answered yet.
 * Used to resume the builder where the user left off.
 */
export function getFirstIncompleteScreen(
  state: PlanBuilderState,
): PlanBuilderScreen {
  const stack = buildPlanBuilderStack(state);

  /** Check if a screen's data has been filled in */
  const isComplete = (screen: PlanBuilderScreen): boolean => {
    switch (screen) {
      case "GoalRecap":
        return state.goal != null;
      case "ModalityScreen":
        return state.modalities.length > 0;
      case "AcceptedPlan":
        return state.acceptedPlanAcknowledged;
      case "PastCycles":
        return state.hasPastCycles != null;
      case "PastCycleDetails":
        return state.pastCycles.length > 0;
      case "Conditions":
        return state.conditions.length > 0;
      case "Ethnicity":
        return state.ethnicity.length > 0;
      case "PartnerStatus":
        return state.partnerStatus != null;
      case "ClinicStatus":
        return state.clinicStatus != null;
      case "KnownResults":
        // Optional — consider complete if they've visited
        return (
          state.knownResults.hasAMH ||
          state.knownResults.hasFSH ||
          state.knownResults.hasSA
        );
      case "LocationInsurance":
        return state.zipCode != null && state.zipCode.length === 5;
      case "Timeline":
        return state.timeline != null;
      default:
        return false;
    }
  };

  // Find the first question screen that isn't answered
  for (const screen of stack) {
    // Stop before non-question screens
    if (
      screen === "FreeText" ||
      screen === "Building" ||
      screen === "PlanReveal"
    ) {
      return screen;
    }
    if (!isComplete(screen)) {
      return screen;
    }
  }

  return stack[0] ?? "GoalRecap";
}

/**
 * Check if the user has any progress (at least one screen answered).
 */
export function hasBuilderProgress(state: PlanBuilderState): boolean {
  return (
    state.goal != null ||
    state.conditions.length > 0 ||
    state.ethnicity.length > 0
  );
}

/**
 * Get progress fraction (0-1) for a given screen.
 */
export function getProgress(
  state: PlanBuilderState,
  current: PlanBuilderScreen,
): number {
  const stack = buildPlanBuilderStack(state);
  const idx = stack.indexOf(current);
  if (idx === -1) return 0;
  // Exclude "Building" from total since it's the completion screen
  const total = stack.length - 1;
  return Math.min(idx / total, 1);
}
